import { SVGProps } from 'react';

import {
  corporate_landing_path,
  deep_research_settings_path,
  edit_user_team_settings_path,
  feed_path,
  for_you_path,
  forum_path,
  gift_landing_path,
  highlight_path,
  logout_path,
  newsletter_list_path,
  opinion_feed_path,
  organization_account_path,
  partners_list_path,
  referrals_path,
  search_page_path,
  settings_path,
  static_jobs_path,
  subscriber_directories_path,
  subscriber_profile_path,
  topic_landing_path,
  video_library_path,
} from '@routes';
import type { NavCurrentUser } from '@utils/backend-api/currentUser';

import SvgIconFacebook from '../Images/Rebranding/IconFacebook';
import SvgIconInstagram from '../Images/Rebranding/IconInstagram';
import SvgIconLinkedIn from '../Images/Rebranding/IconLinkedIn';
import SvgThreads from '../Images/Rebranding/IconThreads';
import SvgIconX from '../Images/Rebranding/IconX';

export type NavItem = {
  name: string;
  href: string;
  description?: string;
  icon?: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  hasExternalLink?: boolean;
};

export type ProNavItem = {
  name: string;
  path?: string;
  href?: string;
};

export type ProNavSections = {
  data_tools: ProNavItem[];
  special: ProNavItem[];
};

export const ACCESS_MONTHLY = 2;

export const ABOUT_PRO_HREF = '/pro-2026';

export const COMMUNITY_SUBMENU_NAV_ITEMS: NavItem[] = [
  {
    name: 'Directory',
    description: 'Search, find and engage with others who are serious about tech and business.',
    href: subscriber_directories_path(),
  },
  {
    name: 'Forum',
    description: 'Follow and be a part of discussions about tech, finance and media.',
    href: forum_path(),
  },
];

export const MORE_SUBMENU_MAIN_NAV_ITEMS: NavItem[] = [
  {
    name: 'Brand Partnerships',
    description: 'Premium advertising opportunities for brands',
    href: partners_list_path(),
  },
  {
    name: 'Group Subscriptions',
    description: 'Team access to our exclusive tech news',
    href: corporate_landing_path(),
  },
  {
    name: 'Newsletters',
    description: 'Journalists who break and shape the news, in your inbox',
    href: newsletter_list_path(),
  },
  {
    name: 'Video',
    description: 'Catch up on conversations with global leaders in tech, media and finance',
    href: video_library_path(),
  },
  {
    name: 'Partner Content',
    description: 'Explore our recent partner collaborations',
    href: highlight_path('partner-content'),
  },
];

export const MORE_SUBMENU_SOCIAL_NAV_ITEMS: NavItem[] = [
  {
    name: 'X',
    href: 'https://x.com/theinformation',
    icon: SvgIconX,
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/gettheinformation',
    icon: SvgIconFacebook,
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/theinformation',
    icon: SvgIconLinkedIn,
  },
  {
    name: 'Threads',
    href: 'https://www.threads.net/@theinformation',
    icon: SvgThreads,
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/theinformation/',
    icon: SvgIconInstagram,
  },
];

export const MORE_SUBNAV_FOOTER_NAV_ITEMS: NavItem[] = [
  {
    name: 'Help & Support',
    href: 'https://theinformation.zendesk.com/hc/en-us',
    hasExternalLink: true,
  },
  {
    name: 'RSS Feed',
    href: feed_path(),
  },
  {
    name: 'Careers',
    href: static_jobs_path(),
  },
];

export const SETTINGS_SUBNAV_MAIN_NAV_ITEMS = (currentUser: NavCurrentUser): NavItem[] => {
  return [
    {
      name: 'Profile',
      href: subscriber_profile_path(currentUser.username),
    },
    {
      name: 'For You',
      href: for_you_path(),
    },
    ...(currentUser.adminOfOrganizationId
      ? [
          {
            name: 'Admin Account',
            href: organization_account_path(),
          },
        ]
      : []),
    {
      name: 'Account Settings',
      href: settings_path(),
    },
    ...(currentUser.isProSubscriber
      ? [
          {
            name: 'Deep Research Settings',
            href: deep_research_settings_path(),
          },
        ]
      : []),
    {
      name: 'Sign out',
      href: logout_path(),
    },
  ];
};

export const SETTINGS_SUBNAV_MORE_NAV_ITEMS: NavItem[] = [
  {
    name: 'Refer a friend',
    href: referrals_path(),
  },
  {
    name: 'Give a subscription',
    href: gift_landing_path(),
  },
  {
    name: 'Set up a team',
    href: edit_user_team_settings_path(),
  },
];

export const MOBILE_SIDE_NAV_ITEMS: NavItem[] = [
  {
    name: 'Search',
    href: search_page_path(),
  },
  {
    name: 'Opinion',
    href: opinion_feed_path(),
  },
  {
    name: 'Venture Capital',
    href: topic_landing_path('venture-capital'),
  },
  {
    name: 'Artificial Intelligence',
    href: topic_landing_path('artificial-intelligence'),
  },
  {
    name: 'Startups',
    href: topic_landing_path('startups'),
  },
  {
    name: 'Market Research',
    href: topic_landing_path('market-research'),
  },
];
